---
title: 'Testing Backend Services'
metaTitle: 'Admin-UI Docs | Tests > Testing Backend Services'
metaDescription: 'How to write and run backend integration and unit tests for feature stack services'
---

Testing strategy for backend services (Commerce Hub API). Based on [Real Database, Real Confidence RFC](https://coveord.atlassian.net/wiki/x/IQCXU) and [ADR-003](https://github.com/coveo-platform/admin-ui/blob/master/docs/adr/003-standardization-of-tests.md).

## Core Principle

**Never mock the database in integration tests.** Use real shadow MySQL for tests touching the data layer. Mock only for pure unit tests.

## Test Types

| Type        | Suffix                    | Database       | Use for                                          |
| ----------- | ------------------------- | -------------- | ------------------------------------------------ |
| Unit        | `.unit.spec.ts`           | None or mocked | Schema validation, utilities, constants          |
| Integration | `.integration.spec.ts`    | Real shadow DB | Any code touching the database                   |

## Fixtures

From `@coveo-platform/commons-service-testing`:

**Real Database (integration tests)**

| Fixture                          | Use for                                          |
| -------------------------------- | ------------------------------------------------ |
| `routeDatabaseTestFixture`       | Route handlers with DB                           |
| `serviceDatabaseTestFixture`     | Service methods with DB                          |
| `serviceIntegrationTestFixture`  | Services with DB + external services             |

**Mocked (unit tests only)**

| Fixture                    | Use for                                   |
| -------------------------- | ----------------------------------------- |
| `serviceUnitTestFixture`   | Pure business logic, no DB                |
| `routeCallerTestFixture`   | Route wiring, file serving, no DB         |

## Integration vs Unit

Use real DB if code touches the database:

| ✅ Integration (real DB)                           | 🙈 Unit (mock OK)                      |
| -------------------------------------------------- | -------------------------------------- |
| Route handler with CRUD                            | Zod schema validation                  |
| Service with Prisma query                          | DTO mapping                            |
| Data layer / repository                            | Pure utilities                         |
| Business logic depending on DB                     | Constants                              |

## Where to Test

**Default: route-level tests.** They cover the full cycle in one pass.

Use service-level tests only when:
- Complex business logic with many edge cases
- Multi-step transactions hard to isolate
- Service reused by multiple routes
- Background workers without routes

Don't duplicate: if route-level test covers it, skip service-level test.

## Database Setup

Automatic via `@coveo-platform/commons-service-testing`:
1. Global setup creates shadow DB and runs migrations
2. Tests use unique IDs to avoid conflicts
3. Global teardown cleans up

Tests must be self-contained: create own data, don't depend on other tests.

## Running Tests

See [Commerce Hub API — Running Tests](/getting-started/10-commerce-hub-api#running-tests).
